/*
 * Design note for this page:
 * Cybernetic Editorial Observatory applied to Singapore-facing legal clarity.
 * The page should feel specific to Singapore operations, PDPA-aware, and commercially
 * readable without sounding like generic compliance boilerplate.
 */

import PolicyLayout from "@/components/PolicyLayout";
import { BellRing, Landmark, ShieldCheck } from "lucide-react";

const cards = [
  {
    title: "PDPA baseline",
    body: "For Singapore-facing operations, Command Secrets treats the Personal Data Protection Act as a core baseline for how personal data is collected, used, disclosed, safeguarded, and retained.",
    icon: Landmark,
  },
  {
    title: "Local notification discipline",
    body: "Singapore users should be able to understand what information is being collected, why it is needed, and which service process it supports before that data is relied upon operationally.",
    icon: ShieldCheck,
  },
  {
    title: "Do Not Call awareness",
    body: "If promotional phone or messaging activity is introduced in the future, communication handling should respect Singapore Do Not Call expectations and channel-specific consent boundaries where applicable.",
    icon: BellRing,
  },
];

const sections = [
  {
    id: "scope",
    title: "Who this Singapore notice is for",
    intro:
      "This page is written for Singapore visitors, customers, citizens, and permanent residents who want the public legal and privacy position stated in Singapore-facing terms.",
    paragraphs: [
      "Command Secrets is positioned for Singapore citizens and Singapore permanent residents, so the public legal posture is intentionally anchored in a Singapore-first framing rather than a generic international template with no local grounding.",
      "This page should be read together with the general privacy notice, support page, security page, cancellation terms, and disclaimer. It is intended to clarify how the service explains data handling, request routes, and customer expectations in a way that aligns with Singapore-facing operations.",
      "The page is descriptive and public-facing. It does not replace mandatory legal notices, narrower customer terms, or any case-specific request-verification steps that may be needed before protected information can be released or changed.",
    ],
  },
  {
    id: "pdpa-position",
    title: "PDPA-oriented collection, use, and disclosure posture",
    intro:
      "The service aims to explain data handling in a way that reflects the core Singapore principle that personal data should be collected, used, and disclosed for legitimate and reasonable purposes rather than for vague or uncontrolled extraction.",
    paragraphs: [
      "Where personal data is collected for account access, support review, billing administration, individualized analysis, report delivery, or security operations, Command Secrets aims to explain the purpose in clear customer-facing language and avoid requesting materially unnecessary information for the chosen service path.",
      "The service may use personal data to operate the website or app, confirm account ownership, deliver reports, answer support requests, investigate abuse, preserve order history, manage disputes, and protect the business where fraud, chargebacks, or suspicious access behaviour must be reviewed.",
      "Disclosure may occur to payment providers, infrastructure vendors, technical operators, legal advisers, or competent authorities where reasonably necessary for service operation, lawful compliance, or rights protection. This page does not promise that disclosure will never occur under any circumstances.",
    ],
  },
  {
    id: "notification-rights",
    title: "Notification, access, correction, and withdrawal handling",
    intro:
      "Singapore users should have a visible path to understand what is happening with their data and how to request review or correction when needed.",
    paragraphs: [
      "Command Secrets aims to provide notice about the categories of data it may handle, the reasons the data may be used, and the support path through which access, correction, or withdrawal-style requests can be raised for review.",
      "Because protected billing, identity, report, and privacy issues can involve sensitive information, the service may require enough verification to confirm that the requester is the correct account holder or authorized payer before action is taken.",
      "A request may be narrowed, paused, or refused where law permits refusal, where identity cannot be verified sufficiently, where the request conflicts with fraud-prevention obligations, or where disclosure would unreasonably affect the rights or security of another person.",
    ],
  },
  {
    id: "communications",
    title: "Communications, support contact, and Do Not Call sensitivity",
    intro:
      "Customer communications should remain tied to support, billing, access, security, or operational delivery needs rather than drifting into uncontrolled outreach.",
    paragraphs: [
      "Support replies, service notices, billing clarifications, release-related operational notices, and privacy follow-up may be sent where reasonably necessary to operate the requested service or protect the account relationship.",
      "If marketing communications, promotional messages, or telephone-based outreach are introduced in the future, Command Secrets expects those channels to include appropriate opt-out handling and to respect Singapore Do Not Call requirements where they apply.",
      "Users who want to limit communication or clarify which messages are operational rather than promotional should use the support path so the request can be reviewed and logged through a controlled manual process.",
    ],
  },
  {
    id: "transfers-security",
    title: "Cross-border processing and local trust expectations",
    intro:
      "A Singapore-facing service may still rely on cloud infrastructure, payment systems, or technical vendors outside Singapore, so local trust depends on controlled safeguards rather than a promise that all processing stays in one jurisdiction.",
    paragraphs: [
      "Command Secrets may store or process personal data through regional or international vendors where this is operationally necessary for hosting, payment, delivery, monitoring, or technical maintenance.",
      "Where cross-border handling occurs, the service aims to use reasonable contractual, technical, and operational protections that fit the service context. The objective is controlled handling, not casual internal visibility or uncontrolled third-party reuse.",
      "Users should also review the security page for the platform's public position on password protection, restricted access, report safeguarding, anti-abuse controls, and service-integrity monitoring.",
    ],
  },
  {
    id: "support-route",
    title: "How Singapore users should raise legal or privacy questions",
    intro:
      "A public legal page only helps users if there is a clear operational route behind it.",
    paragraphs: [
      "Singapore-facing privacy, billing, correction, account, cancellation, and service-integrity requests should be submitted through the support page so the matter can be triaged through a documented review path.",
      "Customers should include the account email, relevant order or invoice reference if available, the affected page or feature, and a concise statement of the request. This is especially important for rights-related or account-sensitive issues.",
      "If Command Secrets materially changes the way Singapore-facing data handling or customer communications work, this page may be revised so the public explanation remains current and commercially understandable.",
    ],
  },
];

const relatedLinks = [
  {
    label: "Privacy notice",
    href: "/legal/privacy",
    description: "Read the broader privacy position that connects Singapore handling to global transparency and rights awareness.",
  },
  {
    label: "UK legal notice",
    href: "/legal/uk",
    description: "Compare the United Kingdom transparency and rights framing used for UK visitors and support requests.",
  },
  {
    label: "Support",
    href: "/support",
    description: "Use the visible support intake flow for privacy requests, billing questions, release concerns, and operational issues.",
  },
  {
    label: "Security",
    href: "/security",
    description: "Review the public-facing security posture, access restrictions, and anti-abuse expectations for the service.",
  },
];

export default function SingaporeLegal() {
  return (
    <PolicyLayout
      eyebrow="Singapore legal notice"
      title="Singapore-facing legal and privacy handling for local users and residents."
      intro="This page explains how Command Secrets frames its legal, privacy, communication, and support posture for Singapore visitors, Singapore citizens, and Singapore permanent residents in public-facing language that stays commercially clear."
      highlight="Explain the purpose, limit the handling, verify the requester, and keep the service controlled."
      cards={cards}
      sections={sections}
      relatedLinks={relatedLinks}
    />
  );
}
